import React from 'react';

import { StyleSheet } from '@lyearn/style';
import { TrackEvent } from '@/components/Analytics/types';
import { Link, LinkProps, TrackableLink } from '@/components/Link';
import { EventContentCard, SkillReview } from '@/modules/content/components/ContentCard';
import {
  DEFAULT_CARD_IMAGE_HEIGHT,
  DEFAULT_CARD_SIZE,
  defaultCardSizeStyles,
} from '@/modules/content/components/ContentCard/DefaultCard/styles';
import {
  DISPLAY_CARD_IMAGE_HEIGHT_MOBILE,
  DISPLAY_CARD_SIZE,
  displayCardSizeStyles,
} from '@/modules/content/components/ContentCard/DisplayCard/styles';
import {
  MINI_CARD_IMAGE_HEIGHT,
  MINI_CARD_IMAGE_WIDTH,
  MINI_CARD_MOBILE_IMAGE_HEIGHT,
  MINI_CARD_MOBILE_IMAGE_WIDTH,
  MINI_CARD_SIZE,
  miniCardSizeStyles,
} from '@/modules/content/components/ContentCard/MiniCard/styles';
import {
  WIDE_CARD_IMAGE_HEIGHT,
  WIDE_CARD_IMAGE_WIDTH,
  WIDE_CARD_SIZE_MOBILE,
  wideCardSizeStyles,
} from '@/modules/content/components/ContentCard/Wide/styles';
import config from '@/modules/content/config';
import { ContentCardTypeEnum } from '@/modules/content/config/cards';
import eventConfig from '@/modules/content/modules/event/config';
import routes from '@/pages/corporate/routes';
import { ContentEntity, EventUnion, ResourceTypeEnum, ReviewCycleType } from '@/types/schema';

import { RESOURCE_EVENT_TYPES } from '../card/utils';
import DisplayCard from '../components/ContentCard/DisplayCard';
import DefaultCardMicroLearning from '../components/ContentCard/MicroLearningCards/DefaultCardMicroLearning';
import DisplayCardMicroLearning from '../components/ContentCard/MicroLearningCards/DisplayCardMicroLearning';
import MiniCardMicroLearning from '../components/ContentCard/MicroLearningCards/MiniCardMicroLearning';
import MiniCard from '../components/ContentCard/MiniCard';
import NewDefaultCard from '../components/ContentCard/NewDefaultCard';
import NewWideCard from '../components/ContentCard/WideCard';
import { LongPressProviderWeb } from '../context/LongPress';
import EventCard from '../modules/event/Card';

interface LinkParams {
  disabled?: boolean;
  query?: Record<string, string | string[]>;
  track?: TrackEvent;
  cardClassName?: string;
}

export type RenderContentCardProps = {
  content: ContentEntity;
  imageProps?: { width: number; height: number };
  cardType: ContentCardTypeEnum;
  isAdminRoute?: boolean;
  params?: LinkParams;
  renderInfo?: () => React.ReactNode;
  accessories?: React.ReactNode;
  actions?: React.ReactNode;
  isMobile?: boolean; //try consider making mandatory later
  target?: string;
  onClick?: () => void;
  isTodosCard?: boolean;
  isDiscoverPage?: boolean;
  noDescription?: boolean;
  widgetId?: string;
};

export const cardImagePropsConfig = {
  [ContentCardTypeEnum.Default]: {
    width: DEFAULT_CARD_SIZE.web.width * 10,
    height: DEFAULT_CARD_IMAGE_HEIGHT.web * 10,
  },
  [ContentCardTypeEnum.Display]: {
    width: DISPLAY_CARD_SIZE.default.width * 10,
    height: DISPLAY_CARD_SIZE.default.width * 10,
  },
  [ContentCardTypeEnum.Mini]: {
    width: MINI_CARD_IMAGE_WIDTH * 10,
    height: MINI_CARD_IMAGE_HEIGHT * 10,
  },
  [ContentCardTypeEnum.Wide]: {
    width: WIDE_CARD_IMAGE_WIDTH * 10,
    height: WIDE_CARD_IMAGE_HEIGHT * 10,
  },
};

export const cardMobileImagePropsConfig = {
  [ContentCardTypeEnum.Default]: {
    width: DEFAULT_CARD_SIZE.mobile.width * 10,
    height: DEFAULT_CARD_IMAGE_HEIGHT.mobile * 10,
  },
  [ContentCardTypeEnum.Display]: {
    width: DISPLAY_CARD_SIZE.mobile.width * 10,
    height: DISPLAY_CARD_IMAGE_HEIGHT_MOBILE * 10,
  },
  [ContentCardTypeEnum.Mini]: {
    width: MINI_CARD_MOBILE_IMAGE_WIDTH * 10,
    height: MINI_CARD_MOBILE_IMAGE_HEIGHT * 10,
  },
  [ContentCardTypeEnum.Wide]: {
    width: WIDE_CARD_IMAGE_WIDTH * 10,
    height: WIDE_CARD_IMAGE_HEIGHT * 10,
  },
};

export const CONTENT_CARD_CLASSES = {
  [ContentCardTypeEnum.Default]: defaultCardSizeStyles,
  [ContentCardTypeEnum.Display]: displayCardSizeStyles,
  [ContentCardTypeEnum.Mini]: miniCardSizeStyles,
  [ContentCardTypeEnum.Wide]: wideCardSizeStyles,
};

export const CONTENT_CARD_STYLES_NATIVE = StyleSheet.create({
  [ContentCardTypeEnum.Default]: {
    width: `${DEFAULT_CARD_SIZE.mobile.width}rem`,
    height: `${DEFAULT_CARD_SIZE.mobile.height}rem`,
  },
  [ContentCardTypeEnum.Display]: {
    width: DISPLAY_CARD_SIZE.mobile.width + 'rem',
  },
  [ContentCardTypeEnum.Mini]: {
    width: `${MINI_CARD_SIZE.mobile.width}rem`,
    height: `${MINI_CARD_SIZE.mobile.height}rem`,
  },
  [ContentCardTypeEnum.Wide]: WIDE_CARD_SIZE_MOBILE,
});

export const getContentCardWidth = (
  type: ContentCardTypeEnum,
  isMobile?: boolean,
  useLegacyDefaultCard?: boolean,
) => {
  let width = 0;

  switch (type) {
    case ContentCardTypeEnum.Display: {
      width = isMobile
        ? DISPLAY_CARD_SIZE.mobile.width
        : useLegacyDefaultCard
        ? DISPLAY_CARD_SIZE.legacy.width
        : DISPLAY_CARD_SIZE.default.width;
      break;
    }
    case ContentCardTypeEnum.Mini: {
      width = isMobile ? MINI_CARD_SIZE.mobile.width : MINI_CARD_SIZE.default.width;
      break;
    }
    case ContentCardTypeEnum.Wide: {
      width = WIDE_CARD_IMAGE_WIDTH;
      break;
    }
    default: {
      width = isMobile ? DEFAULT_CARD_SIZE.mobile.width : DEFAULT_CARD_SIZE.web.width;
      break;
    }
  }

  return width * 10;
};

export const renderContentCard = ({
  content,
  cardType,
  isAdminRoute = false,
  params,
  accessories,
  renderInfo,
  imageProps: _imageProps,
  isMobile,
  target,
  actions,
  onClick,
  isTodosCard = false,
  isDiscoverPage = false,
  noDescription = false,
  widgetId,
}: RenderContentCardProps) => {
  const contentType = content.type;

  const imageProps =
    _imageProps ??
    (isMobile ? cardMobileImagePropsConfig[cardType] : cardImagePropsConfig[cardType]);

  if (!contentType) {
    return null;
  }

  const contentConfig = config[contentType];

  const renderContent = () => {
    if (content.type === ResourceTypeEnum.MicroLearning) {
      if (cardType === ContentCardTypeEnum.Mini) {
        return (
          <MiniCardMicroLearning
            className={params?.cardClassName}
            content={content}
            imageProps={imageProps}
            noDescription={noDescription}
            showPlayButton={true}
          />
        );
      } else if (
        cardType === ContentCardTypeEnum.Display ||
        cardType === ContentCardTypeEnum.Wide
      ) {
        return (
          <DisplayCardMicroLearning
            className={params?.cardClassName}
            content={content}
            imageProps={imageProps}
            noDescription={noDescription}
            showPlayButton={true}
          />
        );
      }
      return (
        <DefaultCardMicroLearning
          className={params?.cardClassName}
          content={content}
          imageProps={imageProps}
          noDescription={noDescription}
          showPlayButton={true}
        />
      );
    }
    if (cardType === ContentCardTypeEnum.Display) {
      return (
        <DisplayCard
          accessories={accessories}
          className={params?.cardClassName}
          content={content}
          disabled={params?.disabled}
          footer={actions}
          imageProps={imageProps}
          isDiscoverPage={isDiscoverPage}
          isTodosCard={isTodosCard}
          noDescription={noDescription}
        />
      );
    }

    if (cardType === ContentCardTypeEnum.Wide) {
      if (RESOURCE_EVENT_TYPES.includes(contentType)) {
        return (
          <EventContentCard.Wide
            accessories={accessories}
            className={params?.cardClassName}
            content={content as any}
            disabled={params?.disabled}
            imageProps={imageProps}
            renderInfo={renderInfo}
          />
        );
      }

      return (
        <NewWideCard
          accessories={accessories}
          actions={actions}
          className={params?.cardClassName}
          content={content}
          disabled={params?.disabled}
          imageProps={imageProps}
          renderInfo={renderInfo}
          onClick={onClick}
        />
      );
    }

    if (!RESOURCE_EVENT_TYPES.includes(contentType)) {
      if (cardType === ContentCardTypeEnum.Mini) {
        return (
          <MiniCard
            className={params?.cardClassName}
            content={content}
            disabled={params?.disabled}
            imageProps={imageProps}
            noDescription={noDescription}
          />
        );
      }

      if (cardType === ContentCardTypeEnum.Default) {
        return (
          <NewDefaultCard
            className={params?.cardClassName}
            content={content}
            imageProps={imageProps}
            noDescription={noDescription}
          />
        );
      }
    }
  };

  const renderedContent = renderContent();

  if (!renderedContent) {
    console.log('[ContentCard] not supported.', { contentType }, { cardType });

    return null;
  }

  if (params?.disabled) {
    return <React.Fragment key={content.id}>{renderedContent}</React.Fragment>;
  }

  const { details, manageDetails } = contentConfig.routes;

  const queryParams = {
    ...params?.query,
  };

  if (content.type === ResourceTypeEnum.MicroLearning) {
    // widgetId query param is used to open the microlearning lesson directly
    queryParams.widgetId = widgetId ?? content.id;
  }

  const parentEntityId = (content as any)?.performance?.parentEntityId;
  if (parentEntityId) {
    queryParams.parentEntityId = parentEntityId;
  }

  let to: LinkProps['to'] = {
    pathname: isAdminRoute ? manageDetails(content.id) : details(content.id),
    query: queryParams,
  };
  if (content.type == ResourceTypeEnum.KnowledgeAsset) {
    // @ts-ignore
    const type = (content.knowledgeAssetSubType ?? '').toLowerCase().replace('_', '-');
    to = {
      pathname: isAdminRoute ? manageDetails(content.id, type) : details(content.id, type),
      query: params?.query,
    };
  }

  if (params?.track) {
    return (
      <TrackableLink key={content.id} className="flex-1" to={to} track={params.track}>
        <LongPressProviderWeb>{renderedContent}</LongPressProviderWeb>
      </TrackableLink>
    );
  }

  return (
    <Link key={content.id} className="flex-1" target={target} to={to}>
      <LongPressProviderWeb>{renderedContent}</LongPressProviderWeb>
    </Link>
  );
};

const getSkillReviewCardComponent = (cardType?: ContentCardTypeEnum) => {
  switch (cardType) {
    case ContentCardTypeEnum.Display:
      return SkillReview.Display;
    case ContentCardTypeEnum.Mini:
      return SkillReview.Mini;
    case ContentCardTypeEnum.Default:
    default:
      return SkillReview.Default;
  }
};

// Skill review card is not yet supported in mobile
// so we don't wrap it in LongPressProviderWeb
export const renderSkillReviewCard = (
  reviewCycle: ReviewCycleType,
  cardType?: ContentCardTypeEnum,
  params?: LinkParams,
) => {
  const Component = getSkillReviewCardComponent(cardType);
  const to = routes.SkillReview(reviewCycle.id);

  return (
    <Link key={reviewCycle.id} className="flex-1" to={to}>
      <Component className={params?.cardClassName} content={reviewCycle} />
    </Link>
  );
};

const EventsCardConfig = {
  DEFAULT: EventCard.Default,
  DISPLAY: EventCard.Display,
  MINI: EventCard.Mini,
  WIDE: () => null,
};

export const getEventCardWidth = (
  type: ContentCardTypeEnum,
  isMobile?: boolean,
  useLegacyDefaultCard?: boolean,
) => {
  let width = 0;

  switch (type) {
    case ContentCardTypeEnum.Display: {
      width = isMobile
        ? DISPLAY_CARD_SIZE.mobile.width
        : useLegacyDefaultCard
        ? DISPLAY_CARD_SIZE.legacy.width
        : DISPLAY_CARD_SIZE.default.width;
      break;
    }
    case ContentCardTypeEnum.Default: {
      width = isMobile ? DEFAULT_CARD_SIZE.mobile.width : DEFAULT_CARD_SIZE.web.width;
      break;
    }
    case ContentCardTypeEnum.Mini: {
      width = isMobile ? MINI_CARD_SIZE.mobile.width : MINI_CARD_SIZE.default.width;
      break;
    }
  }

  return width * 10;
};

interface RenderEventCardProps {
  event: EventUnion;
  cardType: ContentCardTypeEnum;
  imageProps: { width: number; height: number };
  query?: Record<string, string | string[]>;
  useLegacyDefaultCard?: boolean;
  noDescription?: boolean;
}

export const renderEventCard = ({
  event,
  cardType,
  imageProps,
  query,
  useLegacyDefaultCard,
  noDescription = false,
}: RenderEventCardProps) => {
  const Component = EventsCardConfig[cardType];

  return (
    <Link
      key={event.id}
      className="flex-1"
      to={{
        pathname: eventConfig.routes.details(event.id),
        query,
      }}>
      <LongPressProviderWeb>
        <Component
          event={event}
          imageProps={imageProps}
          noDescription={noDescription}
          useLegacyDefaultCard={useLegacyDefaultCard}
        />
      </LongPressProviderWeb>
    </Link>
  );
};
